import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { CaretDown, CaretUp, Check } from "@phosphor-icons/react";
import { motion } from "motion/react";

import { cn } from "../lib/utils";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <CaretDown size={16} className="opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <CaretUp size={16} className="opacity-50" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <CaretDown size={16} className="opacity-50" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

/** Beak/arrow positions mirroring the app-wide rotate-45 tooltip pattern. */
const ARROW_BY_SIDE: Record<string, { className: string; borderStyle: React.CSSProperties }> = {
  // popover sits BELOW the trigger → beak on top edge, pointing up
  bottom: {
    className: "absolute -top-[5px] left-1/2 -translate-x-1/2",
    borderStyle: { borderLeft: "0.5px solid var(--hairline)", borderTop: "0.5px solid var(--hairline)" },
  },
  // popover sits ABOVE the trigger → beak on bottom edge, pointing down
  top: {
    className: "absolute -bottom-[5px] left-1/2 -translate-x-1/2",
    borderStyle: { borderRight: "0.5px solid var(--hairline)", borderBottom: "0.5px solid var(--hairline)" },
  },
  // popover sits to the LEFT of the trigger → beak on right edge, pointing right
  left: {
    className: "absolute -right-[5px] top-5",
    borderStyle: { borderRight: "0.5px solid var(--hairline)", borderTop: "0.5px solid var(--hairline)" },
  },
  // popover sits to the RIGHT of the trigger → beak on left edge, pointing left
  right: {
    className: "absolute -left-[5px] top-5",
    borderStyle: { borderLeft: "0.5px solid var(--hairline)", borderBottom: "0.5px solid var(--hairline)" },
  },
};

const SelectContent = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    /** Render the app-standard rotate-45 beak pointing back at the trigger. */
    arrow?: boolean;
    /**
     * Content pinned ABOVE the scrollable viewport (e.g. a search bar) — stays
     * fixed at the top while the option list below it scrolls.
     */
    stickyHeader?: React.ReactNode;
  }
>(({ className, children, position = "popper", arrow = false, side, stickyHeader, ...props }, ref) => {
  const beak = arrow ? ARROW_BY_SIDE[side ?? "bottom"] : undefined;
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        side={side}
        asChild
        {...props}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          className={cn(
            // No overflow-hidden here so the beak (a child outside the box edge)
            // is not clipped — the inner Viewport owns the scroll/clip instead.
            "relative z-50 max-h-96 min-w-[8rem] rounded-xl border-[0.5px] border-input bg-popover text-popover-foreground shadow-md",
            // With a pinned header, the box becomes a flex column so the header
            // stays put and only the viewport below it scrolls.
            stickyHeader && "flex flex-col",
            position === "popper" &&
              "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
            className
          )}
        >
          {beak && (
            <div
              className={cn("w-2.5 h-2.5 bg-popover rotate-45 z-10", beak.className)}
              style={beak.borderStyle}
            />
          )}
          {/* Pinned header (outside the scroll viewport) — never scrolls away. */}
          {stickyHeader && <div className="shrink-0">{stickyHeader}</div>}
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              "overflow-hidden rounded-xl p-2",
              // Default (no sticky header): preserve the original popper sizing.
              !stickyHeader && position === "popper" &&
                "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
              // With a sticky header: viewport takes the remaining space and scrolls.
              stickyHeader && "min-h-0 flex-1 overflow-y-auto",
              stickyHeader && position === "popper" && "w-full min-w-[var(--radix-select-trigger-width)]"
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </motion.div>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    /**
     * "section" matches the "+ Section" / DropdownMenuItem hover — a subtle
     * muted fill + input-colored border + soft shadow on highlight, instead of
     * the solid accent fill of the default Select item.
     */
    variant?: "default" | "section";
  }
>(({ className, children, variant = "default", ...props }, ref) => {
  const isSection = variant === "section";
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded-lg py-1.5 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        // "section" puts the check on the RIGHT and indents the item content a
        // touch right of the group label (which sits flush-left); the default
        // keeps the conventional left-side check indicator.
        isSection ? "pl-4 pr-8" : "pl-8 pr-2",
        isSection
          ? "border-[0.9px] border-transparent data-[highlighted]:bg-muted/20 data-[highlighted]:border-input data-[highlighted]:shadow-xs"
          : "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
        className
      )}
      {...props}
    >
      <span
        className={cn(
          "absolute flex h-3.5 w-3.5 items-center justify-center",
          isSection ? "right-2" : "left-2"
        )}
      >
        <SelectPrimitive.ItemIndicator>
          <Check size={16} />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
